import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";
import { CreateMediaUploadDocument, SetUserAvatarDocument } from "#app/lib/data";
import { uploadReservedMedia } from "#app/lib/media-upload";

/**
 * Avatar upload pipeline: reserve a photo row + presigned S3 PUT URL, upload
 * the (already-cropped) image bytes directly to S3, then link the photo to
 * the user. The SetUserAvatar response carries the new profileImage URL, so
 * Apollo's normalized User entity (keyFields: userId) updates everywhere
 * without manual cache writes.
 */
export function useAvatarUpload() {
  const [createMediaUpload] = useMutation(CreateMediaUploadDocument);
  const [setUserAvatar] = useMutation(SetUserAvatarDocument);

  const uploadAvatar = useCallback(
    async (userId: string, image: Blob): Promise<boolean> => {
      const contentType = image.type || "image/jpeg";
      try {
        const { data } = await createMediaUpload({
          // sizeBytes is signed into the PUT — the cropper's output blob is
          // exactly what gets uploaded. Avatars are already 512px (cropped
          // client-side), so no thumb variant.
          variables: {
            input: { contentType, kind: "avatar", sizeBytes: image.size },
          },
        });
        const uploaded = await uploadReservedMedia(data?.createMediaUpload, {
          blob: image,
          contentType,
        });
        if (!uploaded.ok) {
          toastManager.add({
            title:
              uploaded.stage === "reserve"
                ? (uploaded.message ?? "Failed to start photo upload")
                : "Failed to upload photo",
            type: "error",
          });
          return false;
        }

        const { data: linkData } = await setUserAvatar({
          variables: { userId, mediaId: uploaded.photoId },
        });
        const result = linkData?.setUserAvatar;
        if (!result || "code" in result) {
          toastManager.add({
            title:
              result && "message" in result && result.message
                ? result.message
                : "Failed to update profile photo",
            type: "error",
          });
          return false;
        }

        toastManager.add({ title: "Profile photo updated", type: "success" });
        return true;
      } catch {
        toastManager.add({ title: "Failed to update profile photo", type: "error" });
        return false;
      }
    },
    [createMediaUpload, setUserAvatar],
  );

  return { uploadAvatar };
}
